/**
 * event-delegation.ts — Document-level event delegation for compiled components.
 *
 * Instead of attaching one `addEventListener` per element per handler (which
 * costs ~2µs each in Chrome — on a 1000-row list with 2 handlers per row
 * that's ~4ms of pure listener-registration overhead on mount), the
 * compiler emits `el.$$click = handler` and installs a single document-level
 * dispatcher via `ensureDelegation()`.
 *
 * The dispatcher walks from `event.target` toward the document root looking
 * for elements with `$$click` (or other delegated event handlers) and invokes
 * the nearest ancestor handler. preventDefault / stopPropagation / the event
 * object itself all work unchanged because we're inside the same native event
 * callback the browser delivered.
 *
 * Scope of delegation (v1):
 *   - `click` only. The event that's attached most frequently in list-row
 *     patterns and has the simplest semantics.
 *   - Bubble-phase only. Capture handlers still use direct addEventListener
 *     (emitted by the compiler on a separate code path).
 *   - Only compiled-path elements. The compat renderer keeps its own event
 *     system.
 *
 * Future events to consider: input, change (with React's onChange remapping),
 * submit, keydown. Each adds ~15 bytes to the runtime per supported event.
 *
 * Safety: `ensureDelegation()` is idempotent — the compiler inserts a call at
 * the top of every file that uses delegated events, and the helper returns
 * early if the listener is already installed in this document. Server/SSR
 * contexts without `document` are also a no-op.
 */
import { batch } from './signal-core.js';
import { syntheticize } from './synthetic-event.js';
// BUG-RUNTIME-154: React hands handlers a synthetic event; this dispatcher
// handed them the raw native one, so `nativeEvent` / `persist` /
// `isDefaultPrevented` / `isPropagationStopped` were undefined. Gated the
// __SPREAD_ONCHANGE__ way: when the whole-graph proof says no source names any
// of the four, this folds to the bare event and synthetic-event.js shakes out.
const __SYNTHETIC_EVENTS__ = typeof __JETSHAKE_SYNTHETIC_EVENTS__ === 'undefined' || __JETSHAKE_SYNTHETIC_EVENTS__;
let _installed = false;
/**
 * Install the document-level click dispatcher if not already installed.
 * Idempotent — safe to call from every compiled file that needs delegation.
 */
export function ensureDelegation() {
    if (_installed)
        return;
    if (typeof document === 'undefined')
        return;
    _installed = true;
    document.addEventListener('click', (event) => {
        // Batch the whole dispatch: multiple setStates across the handler(s) fired by
        // this one event coalesce into a single effect flush + DOM commit, matching
        // React's event batching (an effect keyed to several updated values fires
        // once, not once per setState). Without this, batchDepth stays 0 and every
        // signal write flushes effects immediately, so a handler that calls setState
        // more than once double/triple-fires effects (and re-runs DOM bindings).
        batch(() => {
            let node = event.target;
            try {
                while (node && node !== document) {
                    const handler = node.$$click;
                    if (handler) {
                        // React sets event.currentTarget to the element the handler is attached
                        // to (it changes as the event bubbles). The native event delivered to the
                        // document listener has currentTarget === document, and it's a read-only
                        // accessor on Event.prototype, so we shadow it with a per-node own
                        // property for the duration of this handler. Restored in finally.
                        Object.defineProperty(event, 'currentTarget', { value: node, configurable: true });
                        // Only events that actually reach a compiled handler are augmented -
                        // a click with no `$$click` on its path leaves the document
                        // untouched, as it did before BUG-RUNTIME-154.
                        if (__SYNTHETIC_EVENTS__)
                            syntheticize(event);
                        handler(event);
                        // Respect stopPropagation — if the handler stopped the event, stop walking.
                        // cancelBubble is the legacy sync flag that's set by both stopPropagation()
                        // and stopImmediatePropagation(); it's read the same way by native bubble logic.
                        if (event.cancelBubble)
                            return;
                    }
                    node = node.parentNode;
                }
            }
            finally {
                // Restore the native currentTarget accessor so other native listeners on
                // this event (and post-dispatch reads) see correct browser semantics.
                delete event.currentTarget;
            }
        });
    });
}
